import { useNavigate } from 'react-router';

import { useError } from '@client/hooks/useError';
import { useSubscriptionQueries } from '@client/queries/useSubscriptionQueries';
import { useLoading } from '@client/store/loading.store';
import { EErrorType, type ISubscription, type TErrorData } from '@common/types';

export const useSubscriptions = (activeOnly = false) => {
  const {
    useSubscriptionsList,
    useDeactivateSubscription,
    useSubscriptionsByFeature,
  } = useSubscriptionQueries();
  const { setError } = useError();
  const navigate = useNavigate();

  // Query for user subscriptions
  const { data, refetch, isLoading, isFetching, isError, error } =
    useSubscriptionsList(activeOnly ? 'active' : 'user');

  const { setLoading } = useLoading(false);

  // Mutation for deactivating a subscription
  const deactivateMutation = useDeactivateSubscription();

  const onDeactivate = async (entity: ISubscription): Promise<void> => {
    try {
      setLoading(true);
      await deactivateMutation.mutateAsync(entity.id as string);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_DEACTIVATE,
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (id: string): void => {
    navigate(`/subscriptions/${id}`);
  };

  return {
    loading: isLoading || isFetching,
    isError,
    error,
    entities: data?.subscriptions || [],
    total: data?.total || 0,
    onDeactivate,
    onEdit,
    refetch,
    useSubscriptionsByFeature,
  };
};
